import { Box } from "@mui/material";
import PendingRequestsTable from "./PendingRequestsTable";
import JobsTable from "./JobsTable";
import { jobsContentStyles } from "../../styles/styles";
import { useMediaQuery } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../../Context/AppContext";
import {
  JobModalsManager,
  useJobModalsManager,
} from "../../../../Components/JobModalsManager";
import { useSocketContext } from "../../../../Context/SocketContext/SocketProvider";
import {
  PRODUCT_STATUS,
  ACTIVE_PRODUCT_STATUS_NAMES,
} from "../../../../Constants/productOrderStatus";
import { DONE_SERVICE_STATUS_NAMES } from "../../../../Constants/serviceOrderStatus";
import useProductStatusMap from "../../../../hooks/useProductStatusMap";
import useServiceStatusMap from "../../../../hooks/useServiceStatusMap";
import { useSearchParams } from "react-router-dom";

// Item types backed by the catalog tables (products / restaurants). They share
// the BE endpoints, differing only by the `type` param.
const CATALOG_ITEM_TYPES = ["product", "restaurant"];
// Tabs listed from those endpoints: the catalog verticals plus Coupons, which
// the BE serves as the store + restaurant orders that used a deal (COMY1-359).
const CATALOG_LIST_TABS = [...CATALOG_ITEM_TYPES, "coupon"];
// The job window's views — the My Jobs tab a job is in.
const JOB_VIEWS = ["pending", "active", "completed", "missed"];

export const JobsContent = (props) => {
  const {
    containerRef,
    activeTab,
    pendingRequestsJobsData,
    activeJobsData,
    completedJobsData,
    missedJobsData,

    isPendingJobsLoading,
    isJobsLoading,

    fetchPendingJobs,
    fetchActiveJobs,
    fetchCompletedJobs,
    fetchMissedJobs,
    fetchJobsList,

    hasMoreFor,
    loadMoreFor,
  } = props;
  const isMobile = useMediaQuery("(max-width:767px)");

  // Item-type tab for the Active Jobs & Orders section (desktop). Products is
  // the first/default tab per the COMY1-283 design. Product list data is owned
  // here (service data still comes from the parent hook).
  // A notification deep link to a restaurant order has to land on the
  // Restaurants tab, or the order is never in the list it searches.
  const [searchParams, setSearchParams] = useSearchParams();
  const [itemType, setItemType] = useState(() =>
    searchParams.get("vertical") === "restaurants" ? "restaurant" : "product",
  );
  const [productSectionData, setProductSectionData] = useState([]);
  const [productSectionLoading, setProductSectionLoading] = useState(false);
  // The catalog tab owns its own list, so it owns its own paging cursor too.
  const [productSectionPaging, setProductSectionPaging] = useState({
    page: 1,
    maxPage: 1,
  });

  const [jobsType, setJobsType] = useState("active");
  const prevJobsTypeRef = useRef(jobsType);
  // The view the Active section lists: the status dropdown on desktop, the
  // page-level status tab on mobile. New Jobs is mixed-type, so the item-type
  // tabs never apply to it.
  const jobsView = isMobile ? activeTab : jobsType;
  const isCatalogView =
    CATALOG_LIST_TABS.includes(itemType) && jobsView !== "pending";

  const { containerStyles } = jobsContentStyles;

  // Refetch the catalog list for the current sub-filter (active/completed/missed).
  // Products and restaurants are mirrored on the BE — the item type is passed
  // straight through as the `type` query param, so one loader serves both.
  const loadProductSection = useCallback(async () => {
    if (!isCatalogView || !fetchJobsList) return;
    setProductSectionLoading(true);
    try {
      const res = await fetchJobsList(jobsView, itemType, 1);
      setProductSectionData(res?.data || []);
      setProductSectionPaging({ page: 1, maxPage: res?.max_page_number || 1 });
    } catch (err) {
      console.error(`Failed to load ${itemType} jobs:`, err);
      setProductSectionData([]);
      setProductSectionPaging({ page: 1, maxPage: 1 });
    } finally {
      setProductSectionLoading(false);
    }
  }, [isCatalogView, itemType, fetchJobsList, jobsView]);

  // Append the catalog tab's next page.
  const loadMoreProductSection = useCallback(async () => {
    const nextPage = productSectionPaging.page + 1;
    if (nextPage > productSectionPaging.maxPage) return;
    try {
      const res = await fetchJobsList(jobsView, itemType, nextPage);
      setProductSectionData((prev) => [...prev, ...(res?.data || [])]);
      setProductSectionPaging({
        page: nextPage,
        maxPage: res?.max_page_number || productSectionPaging.maxPage,
      });
    } catch (err) {
      console.error(`Failed to load more ${itemType} jobs:`, err);
    }
  }, [fetchJobsList, jobsView, itemType, productSectionPaging]);

  // Status ids are DB rows; the client works in names and translates through
  // these maps at the socket/API edges (COMY1-283).
  const productStatusMap = useProductStatusMap(itemType);
  const serviceStatusMap = useServiceStatusMap();
  const productStatusMapRef = useRef(productStatusMap);
  const serviceStatusMapRef = useRef(serviceStatusMap);
  useEffect(() => {
    productStatusMapRef.current = productStatusMap;
  }, [productStatusMap]);
  useEffect(() => {
    serviceStatusMapRef.current = serviceStatusMap;
  }, [serviceStatusMap]);

  // Status dropdown change: the row already shows the new status locally, so we
  // DON'T reload the table — except Completing an order, which removes it from
  // the Active filter, so we refetch just for that. The dropdown passes the
  // name too: a Coupons row may be a restaurant order, whose ids aren't in the
  // product map.
  const handleProductStatusUpdated = useCallback(
    (newStatusId, newStatusName) => {
      const name = newStatusName ?? productStatusMap.nameById[newStatusId];
      if (name === PRODUCT_STATUS.COMPLETED) {
        loadProductSection();
      }
    },
    [loadProductSection, productStatusMap],
  );

  // After a job action (approve / decline / status / cancel) refresh every list,
  // INCLUDING the product section — so an approved product order leaves New Jobs
  // and appears in Active right away.
  const handleJobActionComplete = useCallback(() => {
    fetchPendingJobs(isMobile);
    fetchActiveJobs();
    fetchCompletedJobs();
    fetchMissedJobs();
    loadProductSection();
  }, [fetchPendingJobs, fetchActiveJobs, fetchCompletedJobs, fetchMissedJobs, isMobile, loadProductSection]);

  const { openJobModal, modalState, modalHandlers } = useJobModalsManager({
    onJobActionComplete: handleJobActionComplete,
  });
  
  // A notification CTA can deep link to one order — open its modal once the
  // list holding it has loaded. The vertical rides on job.type, so the tab name
  // is all the modal needs here. Same id-field fallback as the realtime handler
  // below: the payload shape varies by source.
  //
  // The CTA navigates in-app, so this page is often already mounted: every new
  // link has to be handled here, not just the one present on mount (COMY1-376).
  const deepLinkOrderId = searchParams.get("productOrder");
  const deepLinkVertical = searchParams.get("vertical");
  // The order may be newer than the lists on screen — reload them once per link.
  const deepLinkRefetchedFor = useRef(null);

  // The chat assistant's job button (COMY1-390) links to
  // /my-jobs?job=<id>&jobType=service|product|restaurant&view=<tab> and opens
  // that job's window straight away, whichever list it is in; the link is
  // dropped once handled so the next button opens its own job.
  const { profileData } = useContext(AppContext);
  const linkedJobId = searchParams.get("job");
  const linkedJobType = searchParams.get("jobType");
  const linkedJobView = searchParams.get("view");
  useEffect(() => {
    if (!linkedJobId || !profileData?.id) return;
    openJobModal(
      { request_id: linkedJobId, type: CATALOG_ITEM_TYPES.includes(linkedJobType) ? linkedJobType : "service" },
      JOB_VIEWS.includes(linkedJobView) ? linkedJobView : "active",
    );
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ["job", "jobType", "view"].forEach((key) => next.delete(key));
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedJobId, linkedJobType, linkedJobView, profileData?.id]);

  // Land on the tab the linked order lives in.
  useEffect(() => {
    if (!deepLinkOrderId) return;
    setItemType(deepLinkVertical === "restaurants" ? "restaurant" : "product");
  }, [deepLinkOrderId, deepLinkVertical]);

  // An order the supplier hasn't accepted yet sits in New Jobs, not the active
  // list, so look there too — opened the way its own "View" button opens it
  // (e.g. the address-changed notification on a pending order).
  useEffect(() => {
    if (!deepLinkOrderId) return;
    const matches = (j) =>
      [j.request_id, j.job_id, j.id]
        .map((v) => String(v))
        .includes(String(deepLinkOrderId));
    const activeJob = (productSectionData || []).find(matches);
    const pendingJob = activeJob
      ? null
      : (pendingRequestsJobsData || []).find(matches);
    const job = activeJob || pendingJob;
    if (job) {
      openJobModal(job, activeJob ? jobsView : "pending");
      // Drop the link once handled, so clicking the same notification again
      // is a fresh navigation rather than a no-op.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("productOrder");
          next.delete("vertical");
          return next;
        },
        { replace: true },
      );
      return;
    }
    if (
      !productSectionLoading &&
      !isPendingJobsLoading &&
      deepLinkRefetchedFor.current !== deepLinkOrderId
    ) {
      deepLinkRefetchedFor.current = deepLinkOrderId;
      fetchPendingJobs(isMobile);
      loadProductSection();
    }
  }, [
    deepLinkOrderId,
    productSectionData,
    pendingRequestsJobsData,
    productSectionLoading,
    isPendingJobsLoading,
    openJobModal,
    jobsView,
    setSearchParams,
    fetchPendingJobs,
    loadProductSection,
    isMobile,
  ]);

  // Real-time: a product order's status changed (member approved / canceled a
  // re-approval, etc.) — refresh the lists so the supplier view updates without
  // a manual reload (COMY1-283). Kept in refs so the handler reads the latest
  // data/tab without re-subscribing on every change.
  const { socket } = useSocketContext();
  const productSectionDataRef = useRef(productSectionData);
  const jobsViewRef = useRef(jobsView);
  useEffect(() => {
    productSectionDataRef.current = productSectionData;
  }, [productSectionData]);
  useEffect(() => {
    jobsViewRef.current = jobsView;
  }, [jobsView]);

  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => {
      const statusId = Number(payload?.status_id);
      const orderId = payload?.order_id;
      // An intra-Active status change (including the supplier's OWN change
      // echoing back) is already reflected in the row, so DON'T reload the
      // whole table. Only refetch when the order actually moves between tabs
      // (member re-approval, completed, canceled, …).
      const inActiveList =
        jobsViewRef.current === "active" &&
        (productSectionDataRef.current || []).some((j) =>
          [j.request_id, j.job_id, j.id]
            .map((v) => String(v))
            .includes(String(orderId)),
        );
      // The catalog NOTIFY payload carries the status NAME alongside the id,
      // so prefer it and fall back to the fetched map.
      const statusName =
        payload?.status || productStatusMapRef.current.nameById[statusId];
      if (inActiveList && ACTIVE_PRODUCT_STATUS_NAMES.includes(statusName)) return;
      handleJobActionComplete();
    };
    // Store and restaurant orders each get their own PG NOTIFY channel (the
    // utils listener keeps the channel name so clients can tell them apart), so
    // subscribing to the products channel alone left restaurant rows stale until
    // a manual refresh.
    const CHANNELS = [
      "product_order_status_update",
      "restaurant_order_status_update",
    ];
    CHANNELS.forEach((ch) => socket.on(ch, handler));
    return () => CHANNELS.forEach((ch) => socket.off(ch, handler));
  }, [socket, handleJobActionComplete]);

  // Real-time: a service order's status changed — most visibly the member
  // paying, which moves the job out of In Progress and into Completed. Without
  // this the supplier kept seeing the paid job in the table until they reloaded
  // the page (COMY1-334). The service payload carries old/new_status_id (the
  // catalog one carries status_id), so it gets its own handler.
  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => {
      const oldStatusId = Number(payload?.old_status_id);
      const newStatusId = Number(payload?.new_status_id);
      // PAYMENT_DONE → CLOSED is the member filling in the feedback survey.
      // The job already sits in Completed, so reloading every list would only
      // flicker the table for nothing.
      const { nameById } = serviceStatusMapRef.current;
      if (
        DONE_SERVICE_STATUS_NAMES.includes(nameById[oldStatusId]) &&
        DONE_SERVICE_STATUS_NAMES.includes(nameById[newStatusId])
      ) {
        return;
      }
      handleJobActionComplete();
    };
    socket.on("service_order_status_update", handler);
    return () => socket.off("service_order_status_update", handler);
  }, [socket, handleJobActionComplete]);

  const desktopServiceData =
    jobsType === "active"
      ? activeJobsData
      : jobsType === "completed"
        ? completedJobsData
        : missedJobsData;

  const mobileData =
    activeTab === "active"
      ? activeJobsData
      : activeTab === "completed"
        ? completedJobsData
        : activeTab === "missed"
          ? missedJobsData
          : pendingRequestsJobsData;

  // The Active section honors the item-type tab on both layouts.
  const isProductSection = isCatalogView;
  const jobsData = isProductSection
    ? productSectionData
    : isMobile
      ? mobileData
      : desktopServiceData;

  // Refetch product list when the Products tab is active and the (jobsType)
  // sub-filter changes.
  useEffect(() => {
    loadProductSection();
  }, [loadProductSection]);

  const handleViewButtonClick = (job, type) => {
    openJobModal(job, type);
  };

  // Which list the Active section is rendering decides where "load more" goes:
  // the catalog tab has its own local cursor, everything else lives in
  // useJobsData. On mobile the section follows the page-level tab instead.
  const jobsTableListKey = jobsView;
  const jobsTableHasMore = isProductSection
    ? productSectionPaging.page < productSectionPaging.maxPage
    : !!hasMoreFor?.(jobsTableListKey);
  const jobsTableLoadMore = useCallback(
    () =>
      isProductSection
        ? loadMoreProductSection()
        : loadMoreFor?.(jobsTableListKey),
    [isProductSection, loadMoreProductSection, loadMoreFor, jobsTableListKey],
  );

  const pendingHasMore = !!hasMoreFor?.("pending");
  const pendingLoadMore = useCallback(
    () => loadMoreFor?.("pending"),
    [loadMoreFor],
  );

  useEffect(() => {
    if (isMobile) return;

    if (prevJobsTypeRef.current !== jobsType) {
      if (jobsType === "active") {
        fetchActiveJobs();
      } else if (jobsType === "completed") {
        fetchCompletedJobs();
      } else if (jobsType === "missed") {
        fetchMissedJobs();
      }
    }

    prevJobsTypeRef.current = jobsType;
  }, [
    jobsType,
    isMobile,
    fetchActiveJobs,
    fetchCompletedJobs,
    fetchMissedJobs,
  ]);

  return (
    <Box sx={containerStyles} ref={containerRef}>
      {!isMobile ? (
        <PendingRequestsTable
          {...{
            containerRef,
            data: pendingRequestsJobsData,
            isLoading: isPendingJobsLoading,
            handleViewButtonClick,
            hasMore: pendingHasMore,
            loadMore: pendingLoadMore,
          }}
        />
      ) : (
        <></>
      )}
      <JobsTable
        {...{
          containerRef,
          activeTab,
          isLoading: isProductSection ? productSectionLoading : isJobsLoading,
          data: jobsData,
          jobsType,
          setJobsType,
          itemType,
          setItemType,
          onJobUpdated: handleProductStatusUpdated,
          handleViewButtonClick,
          hasMore: jobsTableHasMore,
          loadMore: jobsTableLoadMore,
        }}
      />

      <JobModalsManager modalState={modalState} modalHandlers={modalHandlers} />
    </Box>
  );
};
